import { ARMOR_INVULNERABILITY_S, ARMOR_PROTECTS_GROUND, BLINK_HZ, RESPAWN_INVULNERABILITY_S, SIM_HZ } from '../data/tuning';
import type { CoreEvent, CoreWorld, TickContext } from './tick';

/** Countdown name of the post-respawn invulnerability in `world.timers` (ticks). */
export const INVULNERABILITY_TIMER = 'invulnerability';

/** Countdown of the invulnerability that the armor grants when it absorbs a hit (issue #79). */
export const ARMOR_TIMER = 'armor-invulnerability';

/** ARMOR_INVULNERABILITY_S at SIM_HZ: the protected ticks after the hit (90 at 60 Hz). */
export const ARMOR_INVULNERABILITY_TICKS = Math.round(ARMOR_INVULNERABILITY_S * SIM_HZ);

/** RESPAWN_INVULNERABILITY_S at SIM_HZ: checked in phase 5 and decremented in phase 6, so it covers ticks 1..120. */
export const INVULNERABILITY_TICKS = Math.round(RESPAWN_INVULNERABILITY_S * SIM_HZ);

/** Half a blink period: at BLINK_HZ the plane is visible half the period and hidden the other half. */
const BLINK_HALF_PERIOD_TICKS = SIM_HZ / (2 * BLINK_HZ);

/** Called by the respawn (#39). */
export function startInvulnerability(world: CoreWorld): void {
  world.timers[INVULNERABILITY_TIMER] = INVULNERABILITY_TICKS;
}

export function isInvulnerable(world: CoreWorld): boolean {
  return (world.timers[INVULNERABILITY_TIMER] ?? 0) > 0 || (world.timers[ARMOR_TIMER] ?? 0) > 0;
}

/** Where the invulnerability comes from, so the render can show a respawn blink or an armor window (#82). */
export function invulnerabilitySource(world: CoreWorld): 'respawn' | 'armor' | null {
  if ((world.timers[INVULNERABILITY_TIMER] ?? 0) > 0) return 'respawn';
  return (world.timers[ARMOR_TIMER] ?? 0) > 0 ? 'armor' : null;
}

/** Protected ticks left: the next phases 5 that the invulnerability still covers. */
export function invulnerableTicks(world: CoreWorld): number {
  const respawn = world.timers[INVULNERABILITY_TIMER] ?? 0;
  return respawn > 0 ? respawn : Math.max(0, world.timers[ARMOR_TIMER] ?? 0);
}

/** Blink as state, not as render: the render (#53, #67) only reads it. */
export function isBlinkVisible(world: CoreWorld): boolean {
  const remaining = world.timers[INVULNERABILITY_TIMER] ?? 0;
  if (remaining <= 0) return true;
  const elapsed = INVULNERABILITY_TICKS - remaining;
  return Math.floor(elapsed / BLINK_HALF_PERIOD_TICKS) % 2 === 0;
}

/** The bribe is on (GDD §7, issue #80): `bribeRemaining` counts the protected ticks left. */
export function isBribed(world: CoreWorld): boolean {
  return world.bribeRemaining > 0;
}

/** Sources of damage that the bribe cancels: the boss counts as an enemy (GDD §9.1). The ground is not one. */
const BRIBE_CANCELS = new Set(['column', 'enemy', 'projectile', 'boss']);

/**
 * End of phase 5 (GDD §6.1, §7): all the damaging collisions of the tick cost one life at most. The respawn
 * invulnerability ignores obstacles but not the ground, so a plane never rests on it; the invulnerability of
 * the armor ignores the ground too (@gdd-pending). A hit that gets through is absorbed by the armor, which
 * grants its own invulnerability, or costs a life. A lost life moves the level to DERRIBO, where no damaging
 * collision is evaluated (#50); the last one also emits GAME_OVER (#40).
 */
export function resolveDamage(world: CoreWorld, context: TickContext, events: readonly CoreEvent[]): void {
  const respawnWindow = (world.timers[INVULNERABILITY_TIMER] ?? 0) > 0;
  const armorWindow = (world.timers[ARMOR_TIMER] ?? 0) > 0;
  const hits = events.filter((event) => {
    if (event.type !== 'DAMAGING_COLLISION' || event.tick !== world.tick) return false;
    const ground = event.source === 'ground';
    // Cancelled by the bribe before the armor is ever looked at, so it never spends the plate.
    if (isBribed(world) && BRIBE_CANCELS.has(String(event.source))) return false;
    if (respawnWindow && !ground) return false;
    if (armorWindow && (!ground || ARMOR_PROTECTS_GROUND)) return false;
    return true;
  });
  if (hits.length === 0 || world.lives <= 0) return;
  if (world.armor) {
    // The plate is spent; phase 6 counts the timer down on this same tick, hence the extra tick.
    world.armor = false;
    world.timers[ARMOR_TIMER] = ARMOR_INVULNERABILITY_TICKS + 1;
    context.emit({ type: 'powerup:armor-absorbed' });
    return;
  }
  world.lives -= 1;
  world.deaths += 1;
  world.levelState = 'DERRIBO';
  // A life lost ends the bribe (the ground is the only hit it lets through).
  if (isBribed(world)) {
    world.bribeRemaining = 0;
    context.emit({ type: 'powerup:bribe-ended' });
  }
  context.emit({ type: 'LIFE_LOST', livesLeft: world.lives });
  if (world.lives === 0) context.emit({ type: 'GAME_OVER' });
}
